const options: Intl.DateTimeFormatOptions = {
  day: "numeric",
  month: "short",
  year: "numeric",
  timeZone: "UTC",
};

const MS_PER_DAY = 1000 * 60 * 60 * 24;

export const formatDate = (date: string, languageCode: string, useDateSegmentOnly: boolean = false) => {
  if (useDateSegmentOnly) {
    date = date.split("T")[0];
  }
  return new Intl.DateTimeFormat(languageCode, options).format(new Date(date));
};

export const formatDateRange = (startDate: string, endDate: string, languageCode: string): string => {
  return `${formatDate(startDate, languageCode, true)} - ${formatDate(endDate, languageCode, true)}`;
};

export const addDays = (date: Date, days: number): Date => {
  date.setDate(date.getDate() + days);
  return date;
};

export const calculateDateDifference = (startDate: Date, endDate: Date): number => {
  const diffInMs = endDate.getTime() - startDate.getTime();
  return Math.floor(diffInMs / MS_PER_DAY);
};

export const calculateDaysUntilExpiration = (validUpTo: string | null | undefined): number | null => {
  if (!validUpTo) {
    return null;
  }

  // Get today's UTC calendar date (year, month, day)
  const now = new Date();
  const todayYear = now.getUTCFullYear();
  const todayMonth = now.getUTCMonth();
  const todayDay = now.getUTCDate();

  // Parse validUpTo to get expiration calendar date
  let expYear: number, expMonth: number, expDay: number;

  if (validUpTo.includes("T")) {
    if (validUpTo.endsWith("Z") || validUpTo.match(/[+-]\d{2}:\d{2}$/)) {
      // Has timezone indicator (Z or +/-offset), parse normally and extract UTC components
      const expirationDate = new Date(validUpTo);
      expYear = expirationDate.getUTCFullYear();
      expMonth = expirationDate.getUTCMonth();
      expDay = expirationDate.getUTCDate();
    } else {
      // No timezone indicator - treat as UTC (e.g., "2025-11-05T00:00:00")
      // Parse the date part directly to avoid local timezone interpretation
      const datePart = validUpTo.split("T")[0];
      const [year, month, day] = datePart.split("-").map(Number);
      expYear = year;
      expMonth = month - 1; // month is 0-indexed in Date
      expDay = day;
    }
  } else {
    // Date-only string, parse directly (treat as UTC date)
    const [year, month, day] = validUpTo.split("-").map(Number);
    expYear = year;
    expMonth = month - 1; // month is 0-indexed in Date
    expDay = day;
  }

  // Create Date objects at UTC midnight for both dates to calculate difference
  const todayMidnight = new Date(Date.UTC(todayYear, todayMonth, todayDay));
  const expirationMidnight = new Date(Date.UTC(expYear, expMonth, expDay));

  return calculateDateDifference(todayMidnight, expirationMidnight);
};

export const calculateAge = (dateOfBirth?: string): number => {
  if (!dateOfBirth) {
    return 0;
  }

  const [birthYearStr, birthMonthStr, birthDayStr] = dateOfBirth.split("-");
  const birthYear = Number(birthYearStr);
  const birthMonth = Number(birthMonthStr); // 1-12
  const birthDay = Number(birthDayStr);

  const now = new Date();
  const currentYear = now.getUTCFullYear();
  const currentMonth = now.getUTCMonth() + 1; // 1-12
  const currentDay = now.getUTCDate();

  // Determine effective birthday in the current year (UTC)
  let effectiveMonth = birthMonth;
  let effectiveDay = birthDay;

  if (birthMonth === 2 && birthDay === 29) {
    const isLeapYear = (year: number) => (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
    if (!isLeapYear(currentYear)) {
      // In non-leap years, consider Mar 1 as the effective birthday
      effectiveMonth = 3;
      effectiveDay = 1;
    }
  }

  let age = currentYear - birthYear;
  const isBeforeBirthdayThisYear =
    currentMonth < effectiveMonth || (currentMonth === effectiveMonth && currentDay < effectiveDay);
  if (isBeforeBirthdayThisYear) {
    age -= 1;
  }

  return age;
};
